// @flow

import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { ReactComponent as ActivateLogo } from "../../../assets/images/activate-logo.svg";

import Config from "../../constants/Config";
import actions from "../../actions/";
import useBoundingClientRect from "../../hooks/useBoundingClientRect";
import { useScroll } from "../../hooks/useScroll";
import useScrollY from "../../hooks/useScrollY";
import LinkHandler from "../atoms/LinkHandler";
import Icon from "../atoms/Icon";
import Modal from "../organisms/Modal";
import MobileMenuModal from "./MobileMenuModal";
import AccountModal from "./AccountModal";
import QuickSearch from "../molecules/QuickSearch";
import store from "../../store";

const PrimaryNav = (): React.Node => {
    const { scrollDirection } = useScroll();
    const { scrollY } = useScrollY();
    const [customScrollY, setCustomScrollY] = React.useState(scrollY);
    const openDelay = 0;
    const location = useLocation();

    const [showSearchModal, setShowSearchModal] = React.useState(false);
    const [showMobileModal, setShowMobileModal] = React.useState({
        active: false,
        focusSearch: false,
    });

    const classList = ["primary-nav"];
    const { primary_menu } = useSelector((state: any): any => state.options);
    const { loggedIn } = useSelector((state: any): any => state.user);
    const cart = useSelector((state: any): any => state.cart);
    const {
        navHeight,
        navInvert,
        accountModal,
        navCheckout,
        navSimple,
        previousPage,
    } = useSelector((state: any): any => state.ui);
    const scrolledPage = customScrollY > navHeight / 2;

    // Handle updating the nav height
    const primaryNavRef = React.useRef(null);
    const dispatch = useDispatch();
    useBoundingClientRect(primaryNavRef, newHeight => {
        dispatch(actions.setNavHeight(newHeight.height));
    });

    // Dropdown state and bindings
    const [activePrimaryDropdown, setActivePrimaryDropdown] =
        React.useState(null);
    let timeoutIn = React.useRef(null).current;
    let timeoutOut = React.useRef(null).current;
    const [navOpen, setNavOpen] = React.useState(false);
    const [navBlocker, setNavBlocker] = React.useState(false);

    const primaryItemHoverIn = (index: number) => {
        if (navBlocker) {
            return;
        }
        if (timeoutOut) clearTimeout(timeoutOut);

        timeoutIn = setTimeout(
            () => {
                setActivePrimaryDropdown(index);
                setNavOpen(true);
            },
            navOpen ? 0 : openDelay
        );
    };

    const itemHoverOut = () => {
        if (timeoutIn) clearTimeout(timeoutIn);

        timeoutOut = setTimeout(() => {
            setActivePrimaryDropdown(null);
            setNavOpen(false);
        }, 200);
    };

    // on mount use effect (run once)
    React.useEffect((): Function => {
        // subscribe to redux store actions
        store.subscribe(() => {
            // route change action event
            if (
                store.getState().lastAction.type === "@@router/LOCATION_CHANGE"
            ) {
                // close modals
                setShowSearchModal(false);
                dispatch(actions.unopenAccountModal());
                setShowMobileModal({
                    active: false,
                    focusSearch: false,
                });
            }
        });
    }, []); // eslint-disable-line

    // scrollY useEffect
    React.useEffect((): Function => {
        setCustomScrollY(scrollY);
    }, [scrollY]);

    // Close all dropdowns and modals when path changes
    React.useEffect((): Function => {
        // close dropdowns
        setActivePrimaryDropdown(null);

        // close modals
        setShowSearchModal(false);
        dispatch(actions.unopenAccountModal());
        setShowMobileModal({
            active: false,
            focusSearch: false,
        });

        //Prevent Navigation to open just after page reload
        setNavBlocker(true);

        // eslint-disable-next-line
        timeoutIn = setTimeout(() => {
            setNavBlocker(false);

            if (customScrollY < 40) {
                // manualy reset custom scrollY to trigger nav reveal
                setCustomScrollY(0);
            }
        }, 500);
    }, [location.pathname, location.search]);

    // Main menu nav items
    const renderPrimaryLinks = (): React.Node => {
        if (!primary_menu) return null;

        return primary_menu.map((menuItem: any, index: number): React.Node => {
            const hasDropdown =
                menuItem.drop_down_column && menuItem.drop_down_column.length;
            const classList = [""];

            if (hasDropdown) {
                classList.push("has-children");
            }

            if (
                // isLinkActive(menuItem.link.url) ||
                activePrimaryDropdown === index
            ) {
                classList.push("active");
            }

            const openDropdown = () => {
                primaryItemHoverIn(index);
            };

            return (
                <li
                    className={classList.join(" ")}
                    key={index}
                    onMouseOver={hasDropdown ? openDropdown : null}
                    onMouseOut={itemHoverOut}
                    onFocus={hasDropdown ? openDropdown : null}
                    onBlur={itemHoverOut}
                >
                    <LinkHandler
                        to={menuItem.link.url}
                        target={menuItem.link.target}
                        className="primary-nav__top-level-link"
                    >
                        <span
                            className="text"
                            dangerouslySetInnerHTML={{
                                __html: menuItem.link.title,
                            }}
                        />
                        {hasDropdown ? <Icon name="chevron-down" /> : null}
                    </LinkHandler>

                    {/* Dropdown */}
                    {hasDropdown ? (
                        <div
                            className={`primary-nav__dropdown ${
                                activePrimaryDropdown === index
                                    ? "is-active"
                                    : ""
                            }`}
                        >
                            <div className="primary-nav__dropdown__inner">
                                {menuItem.drop_down_column.map(
                                    (
                                        dropDownColumn: any,
                                        index: number
                                    ): React.Node => {
                                        return (
                                            <div
                                                key={index}
                                                className="primary-nav__dropdown__col"
                                            >
                                                <ul>
                                                    {dropDownColumn.main_link ? (
                                                        <li>
                                                            <LinkHandler
                                                                to={
                                                                    dropDownColumn
                                                                        .main_link
                                                                        .url
                                                                }
                                                                target={
                                                                    dropDownColumn
                                                                        .main_link
                                                                        .target
                                                                }
                                                                className="primary-nav__dropdown__main-link"
                                                            >
                                                                <span
                                                                    className="text"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: dropDownColumn
                                                                            .main_link
                                                                            .title,
                                                                    }}
                                                                />
                                                                <Icon name="arrow-right" />
                                                            </LinkHandler>
                                                        </li>
                                                    ) : null}

                                                    {dropDownColumn.sub_links
                                                        ? dropDownColumn.sub_links.map(
                                                              (
                                                                  subLink: any,
                                                                  index: number
                                                              ): React.Node => {
                                                                  return (
                                                                      <li
                                                                          key={
                                                                              index
                                                                          }
                                                                      >
                                                                          <LinkHandler
                                                                              to={
                                                                                  subLink
                                                                                      .link
                                                                                      .url
                                                                              }
                                                                              target={
                                                                                  subLink
                                                                                      .link
                                                                                      .target
                                                                              }
                                                                          >
                                                                              <span
                                                                                  className="text"
                                                                                  dangerouslySetInnerHTML={{
                                                                                      __html: subLink
                                                                                          .link
                                                                                          .title,
                                                                                  }}
                                                                              />
                                                                          </LinkHandler>
                                                                      </li>
                                                                  );
                                                              }
                                                          )
                                                        : null}
                                                </ul>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        </div>
                    ) : null}
                </li>
            );
        });
    };

    if (scrolledPage) {
        classList.push("primary-nav--scrolled");

        if (scrollDirection === "up") {
            classList.push("primary-nav--hide");
        }
    } else {
        classList.push("primary-nav--unscrolled");
    }

    if (activePrimaryDropdown !== null) {
        classList.push("primary-nav--overlay");
    }

    if (navInvert) {
        classList.push("primary-nav--inverted");
    }

    if (navCheckout) {
        classList.push("primary-nav--checkout");
    }

    if (navSimple) {
        classList.push("primary-nav--simple");
    }

    // apply invert class to body for context
    const bodyEl = document.querySelector("body");
    if (bodyEl) {
        if (navInvert || scrolledPage) {
            bodyEl.classList.add("nav-invert");
        } else {
            bodyEl.classList.remove("nav-invert");
        }
    }

    return (
        <>
            <div className={`${classList.join(" ")}`} ref={primaryNavRef}>
                <div className="primary-nav__container container">
                    <div className="primary-nav__inner">
                        <div className="primary-nav__logo">
                            <LinkHandler to="/">
                                <ActivateLogo className="primary-nav__logo__svg" />
                            </LinkHandler>
                        </div>

                        {!navCheckout && !navSimple ? (
                            <>
                                <div className="primary-nav__menu show-for-md">
                                    <nav>
                                        <ul>{renderPrimaryLinks()}</ul>
                                    </nav>
                                </div>
                                <div className="primary-nav__controls">
                                    <PrimaryNavControlBtn
                                        label="profile"
                                        aria-label="profile"
                                        icon={loggedIn ? "user" : "user-login"}
                                        className={`
                                            ${
                                                loggedIn
                                                    ? "primary-nav__user--logged-in"
                                                    : ""
                                            }
                                            ${
                                                location.pathname.startsWith(
                                                    "/my-account"
                                                )
                                                    ? "active"
                                                    : ""
                                            }
                                        `}
                                        onClick={
                                            loggedIn
                                                ? () =>
                                                      dispatch(
                                                          actions.openAccountModal()
                                                      )
                                                : null
                                        }
                                        to={
                                            !loggedIn
                                                ? Config.routes.login
                                                : null
                                        }
                                    />

                                    <PrimaryNavControlBtn
                                        aria-label="cart"
                                        icon="cart"
                                        counter={cart.items_count}
                                        to={Config.routes.cart}
                                    />

                                    {/* Destkop search button */}
                                    <PrimaryNavControlBtn
                                        label="search"
                                        aria-label="search"
                                        icon="search"
                                        className="show-for-md"
                                        onClick={() => setShowSearchModal(true)}
                                    />

                                    {/* Mobile search button */}
                                    <PrimaryNavControlBtn
                                        icon="search"
                                        aria-label="search"
                                        className="hide-for-md"
                                        onClick={() =>
                                            setShowMobileModal({
                                                active: true,
                                                focusSearch: true,
                                            })
                                        }
                                    />

                                    {/* Mobile menu button */}
                                    <PrimaryNavControlBtn
                                        icon="burger"
                                        aria-label="more"
                                        className="hide-for-md"
                                        onClick={() =>
                                            setShowMobileModal({ active: true })
                                        }
                                    />
                                </div>
                            </>
                        ) : null}

                        {navCheckout ? (
                            <div className="primary-nav__checkout-links">
                                {location.pathname.includes(
                                    Config.routes.checkout
                                ) ? (
                                    <LinkHandler
                                        className="icon-link"
                                        to={Config.routes.cart}
                                    >
                                        {/* <Icon name="arrow-left" /> */}
                                        View cart
                                    </LinkHandler>
                                ) : (
                                    <LinkHandler
                                        className="icon-link"
                                        to={
                                            !previousPage ||
                                            previousPage.includes(
                                                Config.routes.cart
                                            ) ||
                                            previousPage.includes(
                                                Config.routes.checkout
                                            ) ||
                                            previousPage.includes(
                                                Config.routes.oktaCallback
                                            )
                                                ? Config.routes.home
                                                : previousPage
                                        }
                                    >
                                        <Icon name="arrow-left" />
                                        Continue <br className="hide-for-sm" />{" "}
                                        Shopping
                                    </LinkHandler>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Desktop Search Modal*/}
            <Modal
                active={showSearchModal}
                // aside={true}
                align="top"
                hideClose={true}
                onClose={() => setShowSearchModal(false)}
                onOverlayClick={() => setShowSearchModal(false)}
            >
                <div className="quick-search__modal">
                    <div className="quick-search__modal__inner">
                        <QuickSearch focusOnMount={true} />
                    </div>
                </div>
            </Modal>

            {/* Mobile Menu Modal */}
            <MobileMenuModal
                active={showMobileModal.active}
                focusSearch={showMobileModal.focusSearch}
                onClose={() =>
                    setShowMobileModal({
                        active: false,
                        focusSearch: false,
                    })
                }
            />

            {/* Profile Menu Modal */}
            <AccountModal
                onClose={() => dispatch(actions.unopenAccountModal())}
                active={accountModal}
            />
        </>
    );
};

const PrimaryNavControlBtn = props => {
    return (
        <LinkHandler
            {...props}
            type="button"
            className={`primary-nav__controls__btn ${props.className || ""}`}
        >
            <Icon name={props.icon} />
            {props.counter ? (
                <div className={`primary-nav__controls__btn__counter`}>
                    {props.counter}
                </div>
            ) : null}
        </LinkHandler>
    );
};

export const PrimaryNavPad = (): React.Node => {
    const navHeight = useSelector((state: any): any => {
        return state.ui.navHeight;
    });
    return <div style={{ height: `${navHeight}px` }}></div>;
};

export default PrimaryNav;
